import React from 'react'
import { styled, breakpointUp, breakpointDown } from '@moonpig/launchpad-utils'
import {
  ScreenReaderOnly,
  CustomLink,
  LinkAsType,
  Flex,
  LoadingIndicator,
} from '@moonpig/launchpad-components'
import {
  IconSystemAvatar,
  IconSystemBasket,
  IconSystemReminder,
  IconSystemHeartOutline,
  IconSystemTickOutline,
} from '@moonpig/launchpad-assets'
import { system as s } from '@moonpig/launchpad-system'
import { useGoogleOneTap } from '@moonpig/web-core-auth'
import { ICON_SIZE_PX } from '../constants'
import { Notifier } from '../Notifier'
import { Tooltip } from '../Tooltip'
import { useLocaleText } from '../locale'

const StyledUtilityMenu = styled.div`
  display: flex;
  top: 4px;
  right: 4px;

  ${s({
    width: { xs: 'auto', md: '100%' },
  })}

  ${breakpointUp('md')} {
    position: relative;
  }
`

const StyledReminder = styled(Flex)`
  flex: 1;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  position: relative;

  ${breakpointDown('md')} {
    ${s({
      minWidth: 'auto',
      width: '44px',
    })}
  }

  ${s({
    color: 'colorInteractionIcon',
    px: 4,
  })}

  .text {
    ${s({
      typography: 'typeBodyCaption',
    })}

    ${breakpointUp('md')} {
      ${s({
        typography: 'typeBodyText',
      })}
    }
  }

  &:hover {
    cursor: pointer;
    text-decoration: none;

    ${s({
      color: 'colorInteractionIconHover',
    })}

    button {
      ${s({
        color: 'colorInteractionIconHover',
      })}
    }
  }
`

const StyledUtilityLink = styled(CustomLink)<{
  count?: number
}>`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  position: relative;

  ${s({
    color: 'colorInteractionIcon',
    px: 4,
  })}

  ${breakpointDown('md')} {
    ${s({
      minWidth: 'auto',
      height: '44px',
      width: '44px',
      margin: '-2px 0',
    })}
  }

  &::after {
    content: ${({ count }) => (count && count > 0 ? `'${count}'` : undefined)};
    ${s({
      bgcolor: 'colorInteractionDefault',
      typography: {
        xs: 'typeMobileDisplay06',
        md: 'typeDesktopBodyCaption',
      },
      color: 'white',
      borderRadius: 7,
    })}
    position: absolute;
    top: 0;
    left: 50%;
    min-width: 20px;
    text-align: center;
    height: 20px;
    line-height: 20px;
  }

  &:focus {
    ${s({ color: 'colorInteractionSelectedState' })};
  }

  &:hover {
    text-decoration: none;

    ${s({
      color: 'colorInteractionIconHover',
    })}
  }
`

const StyledLoadingIndicator = styled(LoadingIndicator)`
  width: ${ICON_SIZE_PX};
  height: ${ICON_SIZE_PX};
`

const AnimatedIconTickIndicator = styled(IconSystemTickOutline)`
  ${s({
    color: 'colorFeedbackSuccess',
  })}

  stroke-dasharray: 80;
  stroke-dashoffset: 80;

  animation: draw 2000ms linear forwards;

  @keyframes draw {
    to {
      stroke-dashoffset: 0;
    }
  }
`

const StyledLabel = styled.span`
  ${s({
    typography: 'typeBodyCaption',
  })}

  ${breakpointUp('md')} {
    ${s({
      typography: 'typeBodyText',
    })}
  }

  ${s({
    display: { xs: 'none', md: 'inline' },
  })}
`

type UtilityLinkProps = {
  to: string
  type: 'reminders' | 'account' | 'basket' | 'favourites'
  linkAs?: LinkAsType
  label: string
  count?: number
  tooltip?: string
}

export type UtilityNotifierProps = {
  notifications: React.ReactNode[]
  onClick: () => void
  onDismiss: (trackEvent: boolean) => void
  showNotificationBadge: boolean
}

export type UtilityMenuProps = {
  items: UtilityLinkProps[]
  notifier?: UtilityNotifierProps
}

const AvatarIcon = ({ label }: { label: string }) => {
  const { oneTapState } = useGoogleOneTap()

  switch (oneTapState) {
    case 'authenticating':
      return <StyledLoadingIndicator label={label} />
    case 'success':
      return (
        <AnimatedIconTickIndicator width={ICON_SIZE_PX} height={ICON_SIZE_PX} />
      )
    default:
      return <IconSystemAvatar width={ICON_SIZE_PX} height={ICON_SIZE_PX} />
  }
}

const getIcon = (
  type: UtilityLinkProps['type'],
  label: UtilityLinkProps['label'],
) => {
  const iconMap = {
    reminders: (
      <IconSystemReminder width={ICON_SIZE_PX} height={ICON_SIZE_PX} />
    ),
    account: <AvatarIcon label={label} />,
    favourites: (
      <IconSystemHeartOutline width={ICON_SIZE_PX} height={ICON_SIZE_PX} />
    ),
    basket: <IconSystemBasket width={ICON_SIZE_PX} height={ICON_SIZE_PX} />,
  }

  return iconMap[type]
}

export const UtilityMenu = ({ items, notifier }: UtilityMenuProps) => {
  const t = useLocaleText()

  return (
    <StyledUtilityMenu>
      {notifier && (
        <StyledReminder>
          <Notifier {...notifier} />
        </StyledReminder>
      )}
      {items.map(({ to, linkAs, label, type, count, tooltip }, index) => (
        <StyledUtilityLink
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          to={to}
          linkAs={linkAs}
          count={count}
          aria-label={label}
        >
          <Flex flexDirection="column" alignItems="center">
            {getIcon(type, label)}
            <StyledLabel aria-hidden>{label}</StyledLabel>
            {count ? (
              <ScreenReaderOnly>
                {t('utility_menu.count', count)}
              </ScreenReaderOnly>
            ) : null}
          </Flex>
          {tooltip && <Tooltip text={tooltip} />}
        </StyledUtilityLink>
      ))}
    </StyledUtilityMenu>
  )
}
